import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { combineLatest } from "rxjs";
import { first, map, switchMap, tap } from "rxjs/operators";
import { PersonFacade } from "src/app/services/person.facade";
import { TemplateFacade } from "src/app/services/template.facade";
import { SubSink } from "subsink";
import {
  AbstractUserService,
  ICirculationTemplate,
  IDefaultStepPayloadData,
  IPerson
} from "../../../../interfaces";
import { LogClass, Logger } from "../../../../logger";
import { NavigationService } from "../../../../services";

@Component({
    selector: "circ-edit-template",
    templateUrl: "./edit-template.component.html",
    styleUrls: ["./edit-template.component.scss"],
    standalone: false
})
@LogClass({ source: "stepForInformation/EditTemplateComponent" })
export class EditTemplateComponent implements OnInit, OnDestroy {
  public form: FormGroup;

  public loading = true;

  private template: ICirculationTemplate;
  private logger: Logger;
  private subscriptions = new SubSink();

  constructor(
    private templateFacade: TemplateFacade,
    private userService: AbstractUserService,
    private navigate: NavigationService,
    private personFacade: PersonFacade
  ) {
    this.logger.debug("ctor");
    this.createForm();
  }

  public abort(): void {
    this.navigateToCurrentTemplate();
  }

  private navigateToCurrentTemplate(): void {
    this.templateFacade
      .getCurrentTemplate()
      .pipe(
        map((t) => t.id),
        first()
      )
      .subscribe((id) => this.navigate.toTemplate(id));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.logger.info("init");
    this.templateFacade
      .getCurrentTemplate()
      .pipe(
        tap((t) => {
          if (!t) {
            throw new Error("no Template given");
          }
        }),
        switchMap((t) => this.templateFacade.getCurrentStep(t)),
        tap((step) => {
          const data = step.payload.data as IDefaultStepPayloadData;

          this.form.setValue({
            title: data?.title,
            comment: data?.comment,
            responsible: [],
            duration: step.duration,
            firstResponseApplies: step.tasks.firstResponseApplies,
            canAddDocuments: step.permissions?.canAddDocuments || false
          });
        }),
        switchMap((step) =>
          this.personFacade.getPersonByLogins(
            step.tasks.assignedTo.map((x) => x.login)
          )
        ),
        first()
      )
      .subscribe((persons: IPerson[]) => {
        this.form.patchValue({
          responsible: persons
        });
        this.loading = false;
      });
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    this.logger.info("saving");

    const users$ = this.userService.getUsers(
      this.form.value.responsible.map((x: IPerson) => x.login)
    );
    const template$ = this.templateFacade.getCurrentTemplate();
    const stepToSave$ = template$.pipe(
      switchMap((t) => this.templateFacade.getCurrentStep(t)),
      map((step) => {
        const data = (step.payload.data || {}) as IDefaultStepPayloadData;
        data.title = this.form.value.title;
        data.comment = this.form.value.comment;
        step.payload.data = data;
        step.duration = this.form.value.duration;
        return step;
      })
    );

    const stepToSet$ = combineLatest([stepToSave$, users$]).pipe(
      map(([step, users]) => {
        step.tasks = {
          assignedTo: users,
          firstResponseApplies: this.form.value.firstResponseApplies
        };
        step.permissions = {
          canAddDocuments: this.form.value.canAddDocuments
        };
        return step;
      })
    );

    combineLatest([template$, stepToSet$])
      .pipe(
        tap(([template, step]) => (template.steps[step.stepNo - 1] = step)),
        switchMap(([template]) => this.templateFacade.save(template))
      )
      .subscribe((id) => {
        this.logger.info("saved:" + id);

        this.navigate.toTemplate(id);
      });
  }

  private createForm() {
    this.form = new FormGroup({
      title: new FormControl(""),
      responsible: new FormControl([]),
      comment: new FormControl(""),
      duration: new FormControl("", [Validators.required]),
      firstResponseApplies: new FormControl(false),
      canAddDocuments: new FormControl(false)
    });
  }
}
